import React, { useRef, useState } from 'react';
import { Card, Button, Table, DateInput, Modal } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { uploadService, bulkDeleteService } from '../../../services/resources';

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const PREVIEW_COLUMNS = [
  { key: 'No', label: 'No' },
  { key: 'Tanggal', label: 'Tanggal', render: (r) => (r.Tanggal ? String(r.Tanggal).slice(0, 10) : '') },
  { key: 'Bank', label: 'Bank' },
  { key: 'Type', label: 'Type' },
  { key: 'Keterangan', label: 'Keterangan' },
  { key: 'Keterangan2', label: 'Keterangan2' },
  { key: 'MataUang', label: 'Mata Uang' },
  { key: 'Valas', label: 'Valas', render: (r) => fmt(r.Valas), className: 'text-right' },
  { key: 'Kurs', label: 'Kurs', render: (r) => fmt(r.Kurs), className: 'text-right' },
  { key: 'NamaCustomer', label: 'Nama Customer' },
  { key: 'Selisih', label: 'Selisih', render: (r) => fmt(r.Selisih), className: 'text-right' },
  { key: 'Debit', label: 'Debit', render: (r) => fmt(r.Debit), className: 'text-right' },
  { key: 'Kredit', label: 'Kredit', render: (r) => fmt(r.Kredit), className: 'text-right' },
  {
    key: 'error',
    label: 'Error Message',
    render: (r) => (r.error ? <span className="text-red-600">{r.error}</span> : ''),
  },
];

export default function UploadPage({ onToast }) {
  const { can } = useAuth();
  const canView = can('UPLOAD', 'CanView');
  const canCreate = can('UPLOAD', 'CanCreate');
  const canDelete = can('UPLOAD', 'CanDelete');

  // --- Bulk delete section state ---
  const [delFrom, setDelFrom] = useState(firstOfMonth());
  const [delTo, setDelTo] = useState(today());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await bulkDeleteService.execute(delFrom, delTo);
      onToast?.(`Deleted ${res.deleted} transaction(s)`);
      setConfirmOpen(false);
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [anyError, setAnyError] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [batchKey, setBatchKey] = useState('');
  const [showErrorOnly, setShowErrorOnly] = useState(false);

  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = '/templates/TemplateUpload.xlsx';
    a.download = 'TemplateUpload.xlsx';
    a.click();
  };

  const pickFile = (f) => {
    setFile(f);
    setRows(null); // reset any prior preview
    setAnyError(false);
    setBatchKey('');
    setShowErrorOnly(false);
  };

  const doParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const data = await uploadService.parse(file);
      setRows(data.rows);
      setAnyError(data.anyError);
      setBatchKey(crypto.randomUUID()); // one key per parsed batch
      setShowErrorOnly(false);
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setParsing(false);
    }
  };

  const doSave = async () => {
    if (!file || anyError || saving) return;
    setSaving(true);
    try {
      const res = await uploadService.save(file, batchKey);
      onToast?.(res.alreadyProcessed ? 'Batch already uploaded' : `Uploaded ${res.inserted} transaction(s)`);
      // Reset after a successful save.
      setFile(null);
      setRows(null);
      setBatchKey('');
      setShowErrorOnly(false);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Upload">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const errorCount = rows ? rows.filter((r) => r.error).length : 0;

  const uploadSection = (
    <Card
      title="Bulk Upload"
      actions={
        <Button variant="secondary" size="sm" onClick={downloadTemplate}>
          Download Template
        </Button>
      }
    >
      {/* Step 1: file picker + Parse */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => pickFile(e.target.files[0] || null)}
          className="text-sm"
        />
        <Button onClick={doParse} disabled={!file || parsing}>
          {parsing ? 'Parsing…' : 'Parse'}
        </Button>
        {rows && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowErrorOnly((v) => !v)}
            disabled={!anyError}
          >
            {showErrorOnly ? 'Show All' : 'Show Error Only'}
          </Button>
        )}
        {rows && (
          <span className={`text-sm ${anyError ? 'text-red-600' : 'text-green-600'}`}>
            {anyError ? `${errorCount} row(s) with errors — fix the file and re-upload` : 'All rows valid'}
          </span>
        )}
      </div>

      {rows && (
        <>
          {/* Step 2: preview */}
          <Table
            columns={PREVIEW_COLUMNS}
            rows={showErrorOnly ? rows.filter((r) => r.error) : rows}
            loading={false}
            emptyText="No rows in file."
            maxHeight="60vh"
            className="rounded-md border border-gray-100"
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {showErrorOnly ? `${errorCount} of ${rows.length} row(s)` : `${rows.length} row(s)`}
            </span>
            {canCreate && (
              <Button onClick={doSave} disabled={anyError || saving || rows.length === 0}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
          {anyError && (
            <p className="mt-2 text-xs text-red-600">
              Save is disabled because the file has validation errors (all-or-nothing).
            </p>
          )}
        </>
      )}
    </Card>
  );

  const bulkDeleteSection = (
    <Card title="Bulk Delete">
      <p className="mb-4 max-w-xl text-sm text-gray-500">
        Delete all transactions within a date range across all four types — typically to clean up a
        bad upload before re-uploading a corrected file. This cannot be undone from the UI.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={delFrom} onChange={setDelFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={delTo} onChange={setDelTo} />
        </div>
        {canDelete && (
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            Delete
          </Button>
        )}
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Bulk Delete"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={doDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Delete all transactions between {delFrom} and {delTo}? This cannot be undone from the UI.
        </p>
      </Modal>
    </Card>
  );

  return (
    <div className="space-y-6">
      {uploadSection}
      {bulkDeleteSection}
    </div>
  );
}
